import React, { useState, KeyboardEvent } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Collapse,
  IconButton,
  SkeletonBlock,
} from "@one-thd/sui-atomic-components";
import { ArrowDown, ArrowUp } from "@one-thd/sui-icons";

export type SortDirection = "asc" | "desc";

export interface PurchaseHistoryColumn<TRow> {
  key: string;
  header: string;
  sortable?: boolean;
  render: (row: TRow) => React.ReactNode;
}

export interface PurchaseHistoryTableProps<TRow> {
  columns: PurchaseHistoryColumn<TRow>[];
  rows: TRow[];
  rowKey: (row: TRow) => string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyMessage?: string;
  /** Rich empty-state content (icon/headline/CTA) rendered instead of `emptyMessage` when there are no rows. Falls back to `emptyMessage` when omitted. */
  emptyState?: React.ReactNode;
  onRowClick?: (row: TRow) => void;
  caption?: string;
  /** Space above the table where each specialized table injects its own search/filter/etc. controls. */
  toolbar?: React.ReactNode;
  /** Space below the table (e.g. a pagination bar). Only rendered once there are rows to paginate. */
  footer?: React.ReactNode;
  /** Renders expanded detail content below a row when its chevron toggle is opened. Provide to enable the expand/collapse row feature (an extra leading chevron column); omit to keep rows non-expandable. */
  renderExpandedRow?: (row: TRow) => React.ReactNode;
  /** Row keys (matching `rowKey`) expanded by default on first render. */
  defaultExpandedRowKeys?: string[];
  /** Key of the column `rows` is currently sorted by; the table only reflects this, it never reorders `rows` itself. */
  sortBy?: string;
  /** Direction `sortBy` is currently sorted in. Defaults to `'asc'`. */
  sortDirection?: SortDirection;
  /** Called with the clicked column's key and its next direction; the caller is responsible for re-sorting/re-fetching `rows`. */
  onSortChange?: (key: string, direction: SortDirection) => void;
}

const SKELETON_ROW_COUNT = 4;

export function PurchaseHistoryTable<TRow>({
  columns,
  rows,
  rowKey,
  isLoading = false,
  isError = false,
  onRetry,
  emptyMessage = "No orders to display.",
  emptyState,
  onRowClick,
  caption,
  toolbar,
  footer,
  renderExpandedRow,
  defaultExpandedRowKeys = [],
  sortBy,
  sortDirection = "asc",
  onSortChange,
}: PurchaseHistoryTableProps<TRow>) {
  // `rowKey` is caller-supplied and not guaranteed unique — e.g. AllOrdersTable's composite key
  // degenerates to just `orderNumber` (or less) when an order is missing enough distinguishing
  // fields, so two different orders can produce the same rowKey. Keying expand/collapse state by
  // rowKey alone would then expand every row sharing that key together. Combining it with the
  // row's position guarantees a unique identity per row within a single render, without touching
  // `rowKey` itself (still used for the React list key's content, `data-row-key`, and
  // defaultExpandedRowKeys, which useRowsInView's lazy-fetch DOM matching depends on verbatim).
  const getExpansionKey = (row: TRow, index: number) => `${index}::${rowKey(row)}`;

  const buildInitialExpansionKeys = () => {
    if (!defaultExpandedRowKeys.length) {
      return new Set<string>();
    }
    const defaults = new Set(defaultExpandedRowKeys);
    const initial = new Set<string>();
    rows.forEach((row, index) => {
      if (defaults.has(rowKey(row))) {
        initial.add(getExpansionKey(row, index));
      }
    });
    return initial;
  };

  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(buildInitialExpansionKeys);
  // Tracks which expanded rows currently show their bordered/padded container. Kept separate
  // from expandedRowKeys because Collapse animates height over ~225ms — removing the border the
  // instant a row starts closing (vs. once fully collapsed) would make the container vanish
  // while content is still visibly shrinking inside it.
  const [borderedRowKeys, setBorderedRowKeys] = useState<Set<string>>(buildInitialExpansionKeys);

  const isExpandable = Boolean(renderExpandedRow);

  const handleSortClick = (key: string) => {
    const nextDirection: SortDirection = key === sortBy && sortDirection === "asc" ? "desc" : "asc";
    onSortChange?.(key, nextDirection);
  };

  const toggleExpanded = (key: string) => {
    setExpandedRowKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Show the bordered container right away when opening; closing removes it only once
        // handleRowCollapsed fires, after the Collapse animation actually finishes.
        setBorderedRowKeys((borderedCurrent) => new Set(borderedCurrent).add(key));
      }
      return next;
    });
  };

  const handleRowCollapsed = (key: string) => {
    setBorderedRowKeys((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const toolbarSlot = toolbar && (
    <div className="sui-flex sui-flex-wrap sui-items-center sui-justify-between sui-gap-3 sui-rounded-t-lg sui-border-1 sui-border-solid sui-border-b-0 sui-border-primary sui-p-3">
      {toolbar}
    </div>
  );

  const footerSlot = footer && (
    <div className="sui-flex sui-flex-wrap sui-items-center sui-gap-3 sui-rounded-b-lg sui-border-1 sui-border-solid sui-border-t-0 sui-border-primary sui-p-3">
      {footer}
    </div>
  );

  // toolbarSlot is deliberately border-top-only (sui-border-b-0), expecting whatever renders
  // below it to close the box with a matching border — normally TableContainer's own. The
  // error/empty states below render plain text instead, so they need that closing border
  // explicitly, or the box looks cut open at the bottom.
  const closingBorder = toolbarSlot
    ? "sui-rounded-b-lg sui-border-1 sui-border-solid sui-border-t-0 sui-border-primary"
    : "";

  if (isError) {
    return (
      <div data-component="PurchaseHistoryTable">
        {toolbarSlot}
        <div className={`sui-flex sui-flex-col sui-items-center sui-gap-3 sui-p-6 sui-text-center ${closingBorder}`}>
          <p className="sui-text-subtle">Something went wrong loading your orders.</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="sui-rounded-base sui-border-1 sui-border-solid sui-border-strong sui-px-4 sui-py-2 sui-text-sm sui-font-bold sui-text-primary hover:sui-bg-subtle"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  const headerRow = (
    <TableHead key="head">
      <TableRow>
        {isExpandable && <TableCell aria-hidden="true" />}
        {columns.map((column) => (
          <TableCell
            key={column.key}
            aria-sort={
              column.sortable
                ? sortBy === column.key
                  ? sortDirection === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
                : undefined
            }
          >
            {column.sortable ? (
              <TableSortLabel
                active={sortBy === column.key}
                direction={sortBy === column.key ? sortDirection : "asc"}
                onClick={() => handleSortClick(column.key)}
              >
                {column.header}
              </TableSortLabel>
            ) : (
              column.header
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div data-component="PurchaseHistoryTable" aria-busy="true">
        {toolbarSlot}
        <TableContainer>
          <Table>
            {headerRow}
            <TableBody>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={index} data-testid="skeleton-row">
                  {isExpandable && <TableCell />}
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      <SkeletonBlock height={4} width="100%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div data-component="PurchaseHistoryTable">
        {toolbarSlot}
        <div className={`sui-p-6 sui-text-center sui-text-subtle ${closingBorder}`}>
          {emptyState ?? <p>{emptyMessage}</p>}
        </div>
      </div>
    );
  }

  const bodyRows = (
    <TableBody key="body">
      {rows.map((row, index) => {
        const key = rowKey(row);
        const expansionKey = getExpansionKey(row, index);
        const isRowExpanded = expandedRowKeys.has(expansionKey);

        return (
          <React.Fragment key={expansionKey}>
            <TableRow
              data-row-key={key}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event: KeyboardEvent<HTMLTableRowElement>) => {
                      // Guards against nested interactive controls (kebab menu, expand chevron)
                      // whose Enter keydown bubbles up here — only their click handlers stop
                      // propagation, not the key event itself.
                      if (event.key === "Enter" && event.target === event.currentTarget) {
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              tabIndex={onRowClick ? 0 : undefined}
              className={
                onRowClick ? "sui-cursor-pointer hover:sui-bg-subtle focus-visible:sui-focus-outline" : undefined
              }
            >
              {isExpandable && (
                <TableCell>
                  <IconButton
                    aria-label={isRowExpanded ? "Collapse row details" : "Expand row details"}
                    aria-expanded={isRowExpanded}
                    onClick={(event: React.SyntheticEvent) => {
                      event.stopPropagation();
                      toggleExpanded(expansionKey);
                    }}
                    icon={isRowExpanded ? ArrowUp : ArrowDown}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell key={column.key}>{column.render(row)}</TableCell>
              ))}
            </TableRow>
            {isExpandable && (
              <TableRow collapsed={!borderedRowKeys.has(expansionKey)}>
                <TableCell colSpan={columns.length + 1}>
                  <Collapse in={isRowExpanded} onExited={() => handleRowCollapsed(expansionKey)}>
                    {renderExpandedRow?.(row)}
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        );
      })}
    </TableBody>
  );

  const tableChildren = [
    caption ? <caption key="caption">{caption}</caption> : null,
    headerRow,
    bodyRows,
  ].filter(Boolean);

  return (
    <div data-component="PurchaseHistoryTable">
      {toolbarSlot}
      <TableContainer>
        <Table>{tableChildren}</Table>
      </TableContainer>
      {footerSlot}
    </div>
  );
}
